import type { FC, ReactElement } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  DropdownTemplate,
  GroupTitleTemplate,
  HubStationsProvider,
  HubStationTemplate,
} from "@ryde/controls";
import { useHubStationsForPair, useStationsOptions } from "@ryde/redux";
import { BOOKING_BULK_FORM_FIELDS, DirectionEnum } from "@ryde/shared";

import {
  useAddedHomeStations,
  useClearStaleHubStation,
  useFromWorkStationsAutofill,
} from "../hooks";
import { BookingHomeStationDropdown } from "./booking-home-station-dropdown";

interface ISourceDestinationDropdownsProps {
  direction: DirectionEnum;
  passengersIds?: number[];
}

export const SourceDestinationDropdowns: FC<
  ISourceDestinationDropdownsProps
> = ({ direction, passengersIds }) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const originStationIdA = watch(BOOKING_BULK_FORM_FIELDS.originStationIdA);
  const destinationStationIdA = watch(
    BOOKING_BULK_FORM_FIELDS.destinationStationIdA,
  );
  const originStationIdB = watch(BOOKING_BULK_FORM_FIELDS.originStationIdB);
  const destinationStationIdB = watch(
    BOOKING_BULK_FORM_FIELDS.destinationStationIdB,
  );
  const hubStationA = watch(BOOKING_BULK_FORM_FIELDS.hubStationA);
  const hubStationB = watch(BOOKING_BULK_FORM_FIELDS.hubStationB);

  const hubStationsA = useHubStationsForPair({
    originStationId: originStationIdA,
    destinationStationId: destinationStationIdA,
    currentHubId: hubStationA,
    direction: DirectionEnum.PickUp,
  });
  const hubStationsB = useHubStationsForPair({
    originStationId: originStationIdB,
    destinationStationId: destinationStationIdB,
    currentHubId: hubStationB,
    direction: DirectionEnum.DropOff,
  });

  useClearStaleHubStation({
    hubFieldName: BOOKING_BULK_FORM_FIELDS.hubStationA,
    hubStations: hubStationsA,
  });
  useClearStaleHubStation({
    hubFieldName: BOOKING_BULK_FORM_FIELDS.hubStationB,
    hubStations: hubStationsB,
  });

  // One instance is enough: `POST /passengers/list-dropdown-dtos-with-stations` is the heaviest
  // reference payload in the app, and `direction` only decides which of these two lists is the
  // "from" and which is the "to" — so the A fields read them one way and the B fields the other.
  const {
    stationsFromDropdownList: homeStations,
    stationsToDropdownList: workStations,
    isDataLoading,
  } = useStationsOptions({
    direction: DirectionEnum.PickUp,
    passengersWatchedPassengerIds: passengersIds,
  });

  const { addedStationIds, onStationQuickAdded } = useAddedHomeStations();

  const {
    onHomeStationASelect,
    onHomeStationAQuickAdded,
    onWorkStationASelect,
  } = useFromWorkStationsAutofill({ direction, onStationQuickAdded });

  const originStationA: ReactElement = (
    <BookingHomeStationDropdown
      name={BOOKING_BULK_FORM_FIELDS.originStationIdA}
      formLabel={t(`booking.formLabel.originStationAId`)}
      homeStations={homeStations}
      addedStationIds={addedStationIds}
      onStationQuickAdded={onHomeStationAQuickAdded}
      onSelect={onHomeStationASelect}
      disabled={isDataLoading}
    />
  );

  const destinationStationA: ReactElement = (
    <DropdownTemplate
      name={BOOKING_BULK_FORM_FIELDS.destinationStationIdA}
      formLabel={t(`booking.formLabel.destinationStationAId`)}
      formAttributes={{
        selectItems: workStations,
        disabled: isDataLoading,
        skipDropdownItemsTranslation: true,
      }}
      onSelect={onWorkStationASelect}
    />
  );

  const originStationB: ReactElement = (
    <DropdownTemplate
      name={BOOKING_BULK_FORM_FIELDS.originStationIdB}
      formLabel={t(`booking.formLabel.originStationBId`)}
      formAttributes={{
        selectItems: workStations,
        disabled: isDataLoading,
        skipDropdownItemsTranslation: true,
      }}
    />
  );

  const destinationStationB: ReactElement = (
    <BookingHomeStationDropdown
      name={BOOKING_BULK_FORM_FIELDS.destinationStationIdB}
      formLabel={t(`booking.formLabel.destinationStationBId`)}
      homeStations={homeStations}
      addedStationIds={addedStationIds}
      onStationQuickAdded={onStationQuickAdded}
      disabled={isDataLoading}
    />
  );

  if (direction === DirectionEnum.Both) {
    return (
      <>
        <GroupTitleTemplate name="stationsA" />
        {originStationA}
        <HubStationsProvider value={hubStationsA}>
          <HubStationTemplate name="hubStationA" />
        </HubStationsProvider>
        {destinationStationA}

        <GroupTitleTemplate name="stationsB" />
        {originStationB}
        <HubStationsProvider value={hubStationsB}>
          <HubStationTemplate name="hubStationB" />
        </HubStationsProvider>
        {destinationStationB}
      </>
    );
  }

  if (direction === DirectionEnum.PickUp) {
    return (
      <>
        {originStationA}
        <HubStationsProvider value={hubStationsA}>
          <HubStationTemplate name="hubStationA" />
        </HubStationsProvider>
        {destinationStationA}
      </>
    );
  }

  if (direction === DirectionEnum.DropOff) {
    return (
      <>
        {originStationB}
        <HubStationsProvider value={hubStationsB}>
          <HubStationTemplate name="hubStationB" />
        </HubStationsProvider>
        {destinationStationB}
      </>
    );
  }

  return null;
};
